import { getIsAuthenticated } from '@/lib/msal-config'
import { queryClient } from '@/lib/query-client'
import { QUERY_KEYS } from '@/lib/api/query-keys'
import type { QueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

const CHANNEL_NAME = 'daisy-proposal-data-v1'

type ProposalDataChanged = {
  type: 'proposal-data-changed'
  quoteId: string
}

export function publishProposalDataChanged(quoteId: string) {
  if (!globalThis.BroadcastChannel) return
  const channel = new BroadcastChannel(CHANNEL_NAME)
  channel.postMessage({ type: 'proposal-data-changed', quoteId } satisfies ProposalDataChanged)
  channel.close()
}

export function subscribeToProposalDataChanges(quoteId: string, onChange: () => void) {
  if (!globalThis.BroadcastChannel) return () => undefined
  const channel = new BroadcastChannel(CHANNEL_NAME)
  const handleMessage = (event: MessageEvent<ProposalDataChanged>) => {
    if (event.data?.type === 'proposal-data-changed' && event.data.quoteId === quoteId) onChange()
  }
  channel.addEventListener('message', handleMessage)
  return () => {
    channel.removeEventListener('message', handleMessage)
    channel.close()
  }
}

export async function invalidateProposalData(client: QueryClient, authenticated: boolean) {
  if (!authenticated) {
    await client.invalidateQueries({ queryKey: QUERY_KEYS.proposal })
    return
  }
  await Promise.all([
    client.invalidateQueries({ queryKey: QUERY_KEYS.quote() }),
    client.invalidateQueries({ queryKey: QUERY_KEYS.paymentMilestones }),
    client.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailsRoot }),
    client.invalidateQueries({ queryKey: QUERY_KEYS.quoteDetailSummaryRoot }),
  ])
}

export function useProposalDataSync(quoteId: string) {
  useEffect(
    () => {
      if (!quoteId) return
      return subscribeToProposalDataChanges(quoteId, () => {
        void invalidateProposalData(queryClient, getIsAuthenticated())
      })
    },
    [quoteId]
  )
}
